import type { MissionControlLog } from "@domain/MissionControl";
import { err, ok, type Result } from "@domain/Result";
import type { IMissionControlLogs } from "@ports/missionControl/IMissionControlLogs";
import type { ITeamMembers } from "@ports/teamMembers/ITeamMembers";

export interface ExportEventLogsUseCasesDeps {
	teamMembersApi: ITeamMembers;
	logsApi: IMissionControlLogs;
}

interface ExportEventLogsInput {
	/** Operator ids derived from shifts overlapping the time window. */
	operatorIds: string[];
	/** Single-shift fallback username when there are no overlapping operators
	 *  but the robot has a current operator (the edge case the original vm
	 *  handled separately). */
	fallbackUsername: string | null;
	timeWindow: { start: Date; end: Date };
}

interface ExportEventLogsResult {
	logs: MissionControlLog[];
	/** Number of distinct mission-control usernames the merge spanned. The
	 *  vm uses this to pluralize "from N operators" in the success toast. */
	usernameCount: number;
}

type ExportEventLogsError =
	/** No overlapping operators AND no fallback username — nothing to query. */
	| { kind: "no_operators" }
	/** Operator ids resolved but none had a `missionControlUsername`. */
	| { kind: "no_usernames" }
	/** Logs query returned [] across every username. */
	| { kind: "no_logs" };

/**
 * Resolves a set of operator ids → mission-control usernames, fetches the
 * event logs for each unique username over the time window, then merges,
 * dedupes, and chronologically sorts the result.
 *
 * Dedup key: `log.id` when present; otherwise the composite
 * `userId::timestamp::eventType`. Per-id and per-username errors are
 * silently absorbed so one failed lookup doesn't tank the whole export.
 * The view-tier handles the actual download + toasts.
 */
export interface ExportEventLogsUseCases {
	run(input: ExportEventLogsInput): Promise<Result<ExportEventLogsResult, ExportEventLogsError>>;
}

async function run(
	deps: ExportEventLogsUseCasesDeps,
	input: ExportEventLogsInput,
): Promise<Result<ExportEventLogsResult, ExportEventLogsError>> {
	// Edge case: no overlapping operators but a current-operator fallback exists.
	if (input.operatorIds.length === 0) {
		if (!input.fallbackUsername) return err({ kind: "no_operators" });
		const logs = await deps.logsApi.getLogsInRange(
			input.fallbackUsername,
			input.timeWindow.start,
			input.timeWindow.end,
		);
		if (logs.length === 0) return err({ kind: "no_logs" });
		const sorted = sortByTimestampAsc(logs);
		return ok({ logs: sorted, usernameCount: 1 });
	}

	// Resolve operator ids → usernames in parallel; absorb individual failures.
	const members = await Promise.all(
		input.operatorIds.map((id) => deps.teamMembersApi.getById(id).catch(() => undefined)),
	);
	const usernames = Array.from(new Set(members.map((m) => m?.missionControlUsername).filter((u): u is string => !!u)));
	if (usernames.length === 0) return err({ kind: "no_usernames" });

	const batches = await Promise.all(
		usernames.map((u) =>
			deps.logsApi
				.getLogsInRange(u, input.timeWindow.start, input.timeWindow.end)
				.catch(() => [] as MissionControlLog[]),
		),
	);

	const seen = new Set<string>();
	const merged: MissionControlLog[] = [];
	for (const log of batches.flat()) {
		const key = String(log.id ?? `${log.userId}::${log.timestamp}::${log.eventType}`);
		if (seen.has(key)) continue;
		seen.add(key);
		merged.push(log);
	}
	if (merged.length === 0) return err({ kind: "no_logs" });

	return ok({ logs: sortByTimestampAsc(merged), usernameCount: usernames.length });
}

export function makeExportEventLogsUseCases(deps: ExportEventLogsUseCasesDeps): ExportEventLogsUseCases {
	return {
		run: (input) => run(deps, input),
	};
}

function sortByTimestampAsc(logs: MissionControlLog[]): MissionControlLog[] {
	return [...logs].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
}
